import React, { useEffect, useState } from 'react'
import { Box, Collapse, Typography } from '@mui/material'
import { TransitionGroup } from 'react-transition-group'
import { useSmartTranslation } from '../hooks/useSmartTranslation'
import SmartPaletteItem from './SmartPaletteItem'
import PaginationControl from './PaginationControl'

const SmartPaletteSection = ({
  section,
  filter,
  renderItem = ({ section, item, label, dragProperties, disabled, onClick, selected, checked, onCheckedChange }) =>
    <SmartPaletteItem
      key={section.id + '-' + label}
      label={label}
      dragProperties={dragProperties}
      disabled={disabled}
      onClick={onClick}
      selected={selected}
      checked={checked}
      onCheckedChange={onCheckedChange}
    />,
  getLabel = (section, item) => item?.name,
  isItemDisabled = (section, item) => false,
  areSameItem = (section1, item1, section2, item2) => section1?.id === section2?.id && item1?.id === item2?.id,
  getDragProperties = (section, item) => { return false },
  onItemClick = (section, item) => {},
  isItemChecked = (section, item) => {},
  onCheckedChange = (section, item, checked) => {},
  selectedSection = null,
  selectedItem = null,
  pageable = false,
  itemsPerPage = 5,
  sx = {},
  forceReload = null,
  searchByCodeName = false
}) => {
  const { t_ } = useSmartTranslation()
  const [pageableObject, setPageableObject] = useState({ number: 1, size: itemsPerPage, filter })
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState(
    section.items && typeof section.items !== 'function'
      ? pageable
        ? section.items.slice((pageableObject.number - 1) * pageableObject.size, (pageableObject.number - 1) * pageableObject.size + pageableObject.size)
        : section.items
      : [])

  const [totalElements, setTotalElements] = useState(section.items ? section.items.length : 0)
  const result = new Map()
  result.set('name', ['code'])
  const [requestPagination, setRequestPagination] = useState({
    parameterFilters: [],
    parameterFiltersValues: [],
    parameterFiltersNames: [],
    columns: ['id', 'code', 'name'],
    page: pageableObject.number - 1,
    size: pageableObject.size,
    columnFilters: ['name'],
    columnFilterValues: filter ? [filter.toLowerCase()] : null,
    sortKey: [],
    sortMode: [],
    rows: [],
    primaryKeys: ['id'],
    allSelected: false,
    mixedColumnFilters: searchByCodeName ? JSON.stringify(Object.fromEntries(result)) : null
  })

  useEffect(() => {
    if (section.items !== undefined && section.items !== null) {
      if (typeof section.items !== 'function') {
        const start = (requestPagination.page) * requestPagination.size
        setItems(pageable ? section.items.slice(start, start + requestPagination.size) : section.items)
        setTotalElements(section.items ? section.items.length : 0)
      } else {
        setLoading(true)
        section.items(requestPagination).then((data) => {
          setItems(pageable ? data.content : data)
          setTotalElements(pageable ? data.totalElements : data.length)
        }).catch(() => {
          console.error('Error getting items')
        }).finally(() => {
          setLoading(false)
        })
      }
    }
  }, [pageable, section, requestPagination, forceReload])

  useEffect(() => {
    setRequestPagination(prev => ({
      ...prev,
      page: pageableObject.number - 1,
      columnFilterValues: pageableObject.filter ? [pageableObject.filter.toLowerCase()] : null
    }))
  }, [pageableObject])

  useEffect(() => {
    setPageableObject(prev => ({ ...prev, number: 1, filter }))
  }, [filter])

  const handlePageChange = (page) => {
    setPageableObject(prev => ({ ...prev, number: page }))
  }

  if (section.customRender) {
    return section.customRender(section, items, getDragProperties)
  }

  return (
    <Box>
      {items && items.length > 0
        ? (
          <TransitionGroup
            style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}
          >
            {items.map((item) =>
              <Collapse key={section.id + '-' + getLabel(section, item)}>
                {renderItem({
                  section,
                  item,
                  label: getLabel(section, item),
                  dragProperties: getDragProperties(section, item),
                  disabled: isItemDisabled(section, item),
                  onClick: () => onItemClick(section, item),
                  selected: areSameItem(selectedSection, selectedItem, section, item),
                  checked: isItemChecked(section, item),
                  onCheckedChange: (checked) => onCheckedChange(section, item, checked)
                })}
              </Collapse>
            )}
            {pageable && items && totalElements > itemsPerPage
              ? (
                <Box sx={{ textAlign: 'center' }}>
                  <PaginationControl
                    totalItems={totalElements}
                    itemsPerPage={itemsPerPage}
                    page={pageableObject.number}
                    onPageChange={page => handlePageChange(page)}
                  />
                </Box>
                )
              : <></>}
          </TransitionGroup>
          )
        : (
            loading ? <></> : <Typography color='error'>{t_('Not available')}</Typography>
          )}
    </Box>
  )
}

export default SmartPaletteSection
